// Cycle 23 L (Madde 13) + L2 hotfix — deep COD-form cart bridge for
// mixed-variant bundles.
//
// The C1 standard contract (change events + ?variant= URL + [name=quantity])
// can only express ONE variant × quantity. Mixed-variant bundle tiers and gift
// lines need the FULL selection mirrored into the COD app's Storefront-API
// cart via the app's window-level surface (Releasit COD Form's undocumented
// `window._rsiV2` — getState/cartService.{addProduct,removeProduct,getCart,
// applyDiscount}). The app builds its order FROM that cart (App Proxy
// create-order-new posts the cartId), so correct lines in → correct order out.
//
// L2 hotfix (live run d8809bf9, root causes read from the app's deminified
// production bundle):
//   - the app's own clearCart throws "Error removing cart lines." whenever
//     cartLinesRemove reports userErrors, and our C1 signals trigger the
//     app's OWN in-flight cart rewrite at click time, so its remove races
//     stale line ids structurally → the bridge clears with the app's
//     TOLERANT removeProduct instead (it never inspects userErrors; a stale
//     id just means the line is already gone), bounded to two passes,
//   - the bridge write defers BRIDGE_DELAY_MS past the click so the app's
//     click-time cart work settles before we touch the cart,
//   - getCart resolves {data:{cart:{lines:{edges}}}} — extractLines never
//     matched that shape, so re-verify was silently skipped in production,
//   - applyDiscount spreads its second argument ([...codes]) — it takes an
//     ARRAY of codes; a bare string explodes into characters.
//
// L3 hotfix (live run 9bc1e95c): the cart write landed (no toast, re-verify
// green) but the form AND the created order still showed the stale single
// line — the app renders the form and builds the order body from its React
// state `cartInfo` (order attrs iterate state.lines.edges), never re-reading
// the Storefront cart after an external write. The app's own bundle-app
// integration precedent solves this exactly one way — setState({cartInfo:
// cartLinesAdd.cart}) after writing lines (the provider re-derives its
// products list from cartInfo.lines.edges[].node.merchandise) — so the
// bridge now does the same through the exposed _rsiV2.setState.
//
// Defensive by design — this asset must NEVER break the PDP or the button:
//   - strict feature-detection: not a single call unless the surface shape
//     matches exactly; any drift → console.warn + the C1 standard contract
//     stays in charge (approximate open, never broken),
//   - everything try/catch,
//   - theme-editor kill-switch: <bundle-selector data-cod-bridge="false">
//     (section setting `cod_deep_sync`) disables the bridge with no redeploy,
//   - bounded retry: ONE delayed re-verify, ONE repair, then stop — no loops.
//
// NOTE: the app's legacy `_RSI_COD_FORM_OVERWRITE_ATC_ITEMS` hook was removed
// from its current bundle — deliberately NOT used here.
(function () {
  'use strict';

  // The app injects its buy button dynamically (app embed), so a
  // document-level CAPTURE-phase delegation is the only reliable hook.
  var COD_BUTTON_SELECTOR =
    '#rsi_buy_now_button, ._rsi-buy-now-button, #floatingBuyNowButton, [href="/rsi-btn-hook"]';
  var BRIDGE_DELAY_MS = 400;
  var REVERIFY_DELAY_MS = 300;

  function warn(detail) {
    console.warn(
      'cod-bridge: app surface changed/failed, falling back to standard contract',
      detail
    );
  }

  // Kill-switch — a missing attribute (pre-L bake) counts as enabled.
  function killSwitchOff(el) {
    return !!(el && el.dataset && el.dataset.codBridge === 'false');
  }

  // Deep integration is only worth the exposure when the single-variant form
  // contract cannot express the selection. Single unit, no gifts, not mixed →
  // C1 already carries the exact selection; the bridge stays out.
  function inScope(snapshot) {
    return snapshot.mainQuantity > 1 || snapshot.gifts.length > 0 || snapshot.mixed === true;
  }

  // Feature-detect the app surface — exact shape or nothing. The tolerant
  // clear needs getCart + removeProduct up front (the app's clearCart is
  // deliberately NOT used — see the header); setState is required too: a
  // state-blind write provably changes nothing the customer sees (L3).
  // applyDiscount stays optional at its point of use.
  function detectSurface() {
    var rsi = typeof window !== 'undefined' ? window._rsiV2 : null;
    if (!rsi || typeof rsi.getState !== 'function' || typeof rsi.setState !== 'function') {
      return null;
    }
    var cartService = rsi.cartService;
    if (
      !cartService ||
      typeof cartService.addProduct !== 'function' ||
      typeof cartService.removeProduct !== 'function' ||
      typeof cartService.getCart !== 'function'
    ) {
      return null;
    }
    return { rsi: rsi, cartService: cartService };
  }

  function toGid(variantId) {
    return 'gid://shopify/ProductVariant/' + variantId;
  }

  // Aggregate the snapshot into unique cart lines (same variant → one line
  // with the summed quantity): every unit counts 1, gifts carry their own qty.
  function buildLines(snapshot) {
    var order = [];
    var byId = {};
    function add(variantId, qty) {
      var key = String(variantId);
      if (!byId[key]) {
        byId[key] = { merchandiseId: toGid(variantId), quantity: 0 };
        order.push(key);
      }
      byId[key].quantity += qty;
    }
    snapshot.units.forEach(function (u) {
      add(u.variantId, 1);
    });
    snapshot.gifts.forEach(function (g) {
      add(g.variantId, g.quantity || 1);
    });
    return order.map(function (key) {
      return byId[key];
    });
  }

  // Unwrap the getCart return value into a plain array of line nodes — the
  // shape is the app's internal contract: {data:{cart:{lines:{edges}}}} in
  // its current bundle, plus the two shapes the bridge already tolerated.
  // Anything else → null (callers warn, never guess).
  function unwrapLines(cart) {
    if (!cart) return null;
    var lines =
      cart.lines ||
      (cart.cart && cart.cart.lines) ||
      (cart.data && cart.data.cart && cart.data.cart.lines) ||
      null;
    if (lines && Array.isArray(lines.edges)) {
      lines = lines.edges.map(function (edge) {
        return edge && edge.node;
      });
    }
    return Array.isArray(lines) ? lines : null;
  }

  // Tolerant {merchandiseId, quantity} extractor for re-verify.
  function extractLines(cart) {
    var lines = unwrapLines(cart);
    if (!lines) return null;
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      if (!line) return null;
      var merchandiseId =
        (line.merchandise && line.merchandise.id) || line.merchandiseId || null;
      var quantity = typeof line.quantity === 'number' ? line.quantity : Number(line.quantity);
      if (!merchandiseId || !isFinite(quantity)) return null;
      out.push({ merchandiseId: String(merchandiseId), quantity: quantity });
    }
    return out;
  }

  // Compare as id → quantity maps (order-insensitive).
  function sameLines(expected, actual) {
    if (expected.length !== actual.length) return false;
    var want = {};
    expected.forEach(function (l) {
      want[l.merchandiseId] = l.quantity;
    });
    for (var i = 0; i < actual.length; i++) {
      if (want[actual[i].merchandiseId] !== actual[i].quantity) return false;
    }
    return true;
  }

  // Current cart line ids for the tolerant clear. null → unknown shape.
  function readCartLineIds(cartService, cartId) {
    return Promise.resolve(cartService.getCart(cartId)).then(function (cart) {
      var lines = unwrapLines(cart);
      if (!lines) return null;
      var ids = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        if (line && line.id) ids.push(String(line.id));
      }
      return { ids: ids, lines: lines };
    });
  }

  // Tolerant clear + add: read line ids → removeProduct (userErrors ignored —
  // a stale id means the line is already gone) → ONE re-read → remove any
  // leftovers once more → addProduct. Two remove passes max, never a loop.
  function applyLines(cartService, cartId, lines) {
    return readCartLineIds(cartService, cartId).then(function (read) {
      if (read === null) {
        // Can't see the cart → can't clear safely; a blind add would stack
        // onto the app's own line. Reject into the C1 fallback.
        return Promise.reject('getCart returned an unknown shape — bridge write skipped');
      }
      var cleared =
        read.ids.length === 0
          ? Promise.resolve()
          : Promise.resolve(cartService.removeProduct(cartId, read.ids))
              .then(function () {
                return readCartLineIds(cartService, cartId);
              })
              .then(function (second) {
                if (second && second.ids.length > 0) {
                  return cartService.removeProduct(cartId, second.ids);
                }
              });
      return cleared.then(function () {
        return cartService.addProduct(cartId, lines);
      });
    });
  }

  // Tolerant reader for the applyDiscount mutation result — only used to
  // surface userErrors as a warn (the lines are already correct either way).
  function discountUserErrors(result) {
    if (!result) return null;
    var payload =
      (result.data && result.data.cartDiscountCodesUpdate) ||
      result.cartDiscountCodesUpdate ||
      null;
    if (payload && Array.isArray(payload.userErrors) && payload.userErrors.length > 0) {
      return payload.userErrors;
    }
    return null;
  }

  function applyDiscountOnce(cartService, cartId, snapshot) {
    if (!snapshot.discountCode) return;
    if (typeof cartService.applyDiscount !== 'function') {
      warn('applyDiscount missing on app surface — discount not mirrored');
      return;
    }
    // The app spreads its second argument ([...codes].sort()) — pass an
    // ARRAY of codes, never a bare string. Discount failure keeps the
    // (already correct) lines — warn only. The mutation result is returned
    // so syncAppState can prefer its (freshest) cart object.
    return Promise.resolve(cartService.applyDiscount(cartId, [snapshot.discountCode]))
      .then(function (result) {
        var errs = discountUserErrors(result);
        if (errs) warn(errs);
        return result;
      })
      .catch(function (err) {
        warn(err);
      });
  }

  // Tolerant cart-object extractor for mutation / getCart responses — the
  // setState({cartInfo}) sync needs the full cart node, wherever the
  // response nests it. A cart without lines is useless to the provider's
  // products derivation → treated as not found.
  function unwrapCartObject(result) {
    if (!result) return null;
    var data = result.data || result;
    var cart =
      (data.cartDiscountCodesUpdate && data.cartDiscountCodesUpdate.cart) ||
      (data.cartLinesAdd && data.cartLinesAdd.cart) ||
      data.cart ||
      null;
    return cart && cart.lines ? cart : null;
  }

  // Sync the app's React state with the cart we just wrote — the form renders
  // and the order body is built from state.cartInfo, never re-read from the
  // Storefront cart (L3 root cause). Mirrors the app's own bundle-app
  // integration precedent: setState({cartInfo: <mutation cart>}). Cart object
  // priority: discount-update response (freshest) → cartLinesAdd response →
  // a fresh getCart. Failure degrades with a warn — the cart itself stays
  // correct, the bridge never breaks the button.
  function syncAppState(rsi, cartService, cartId, addResult, discountResult) {
    var cart = unwrapCartObject(discountResult) || unwrapCartObject(addResult);
    var resolved = cart
      ? Promise.resolve(cart)
      : Promise.resolve(cartService.getCart(cartId)).then(unwrapCartObject);
    return resolved
      .then(function (cartInfo) {
        if (!cartInfo) {
          warn('no cart object to sync into app state — form may show stale lines');
          return;
        }
        return Promise.resolve(rsi.setState({ cartInfo: cartInfo })).catch(warn);
      })
      .catch(warn);
  }

  // ONE re-verify after a short delay (the app's own variant watcher may have
  // raced our write), ONE repair, then stop — second mismatch only warns. A
  // successful repair re-syncs the app state (same L3 rule as the first write).
  function verifyOnce(surface, cartId, lines, attempt) {
    var cartService = surface.cartService;
    Promise.resolve(cartService.getCart(cartId))
      .then(function (cart) {
        var actual = extractLines(cart);
        if (actual === null) {
          warn('getCart returned an unknown shape — re-verify skipped');
          return;
        }
        if (sameLines(lines, actual)) return;
        if (attempt >= 1) {
          warn('cart lines still mismatched after one repair — leaving as-is');
          return;
        }
        return applyLines(cartService, cartId, lines).then(function (repairAddResult) {
          return syncAppState(surface.rsi, cartService, cartId, repairAddResult, null).then(
            function () {
              setTimeout(function () {
                verifyOnce(surface, cartId, lines, attempt + 1);
              }, REVERIFY_DELAY_MS);
            }
          );
        });
      })
      .catch(warn);
  }

  function bridgeWrite(snapshot) {
    var surface = detectSurface();
    if (!surface) {
      warn('feature-detect: _rsiV2 surface shape mismatch');
      return;
    }
    // cartId is read AFTER the delay — the app's click-time work may have
    // rebuilt its cart in the meantime.
    var state = surface.rsi.getState();
    var cartId = state && state.cartInfo && state.cartInfo.id;
    if (!cartId) {
      warn('no cartInfo.id in app state');
      return;
    }
    var lines = buildLines(snapshot);
    var addResult;
    applyLines(surface.cartService, cartId, lines)
      .then(function (result) {
        addResult = result;
        return applyDiscountOnce(surface.cartService, cartId, snapshot);
      })
      .then(function (discountResult) {
        return syncAppState(surface.rsi, surface.cartService, cartId, addResult, discountResult);
      })
      .then(function () {
        setTimeout(function () {
          verifyOnce(surface, cartId, lines, 0);
        }, REVERIFY_DELAY_MS);
      })
      .catch(warn);
  }

  function runBridge(selectorEl) {
    var snapshot =
      typeof selectorEl.getSelectionSnapshot === 'function'
        ? selectorEl.getSelectionSnapshot()
        : null;
    if (!snapshot) return;
    if (!inScope(snapshot)) return;

    // The snapshot is frozen at click time (what the customer saw), but the
    // write waits out the app's own click-time cart work — writing
    // immediately races its in-flight rewrite (live run d8809bf9).
    setTimeout(function () {
      try {
        bridgeWrite(snapshot);
      } catch (err) {
        warn(err);
      }
    }, BRIDGE_DELAY_MS);
  }

  function onCodClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      if (!target.closest(COD_BUTTON_SELECTOR)) return;
      // No bundle-selector on this page → nothing to bridge.
      var selectorEl = document.querySelector('bundle-selector');
      if (!selectorEl || killSwitchOff(selectorEl)) return;
      runBridge(selectorEl);
    } catch (err) {
      warn(err);
    }
  }

  document.addEventListener('click', onCodClick, true);
})();
